import React, { useContext, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { HullLayer } from './HullLayer'
import { HullScopeContext, HullStore } from './hullScope'

export interface SelectionHullProps {
  /** Alle Zeilen der Liste in der Reihenfolge, in der sie dastehen. */
  rowIds: readonly string[]
  selectedIds: ReadonlySet<string>
  /** Ruhende Zeilen. Steinfarben wird ein Block nur, wenn alle seine Zeilen ruhen. */
  dormantIds?: ReadonlySet<string>
  /** Die aufgeklappte bzw. gerade bearbeitete Zeile: Sie ist ihre eigene Karte und bleibt draußen. */
  excludedId?: string | null
  /** Das Attribut, das eine Zeile mit ihrer Kennung trägt, z. B. `data-task-id`. */
  rowAttribute: string
  /** Was an der Zeile gemessen wird, wenn nicht das Element mit dem Attribut selbst (`.task-entry`). */
  rowSelector?: string
  /** Aufgaben: Squircle 40px. Unteraufgaben: 10px wie ihre Zeilen. */
  kind?: 'task' | 'subtask'
  /**
   * Während eines Zugs (und beim Landen) keine Hülle: Die Zeilen wandern per `transform`, den Weg
   * sähe die Hülle nicht. Die Liste lässt die Zeilen ihre Markierung dann selbst malen.
   */
  suspended?: boolean
}

/**
 * **Die Auswahl-Hülle** (#293): eine Fläche hinter markierten Nachbarzeilen. Sie wächst beim
 * Dazunehmen, schrumpft beim Abwählen, teilt sich und verschmilzt wieder. Die Zeilen in so einer
 * Liste malen ihre Markierung nicht selbst.
 *
 * **Liegt als erstes Kind in der Liste** (`position: relative`) und malt dort ihre eigene Ebene
 * (`HullLayer`: Messen, Planen, Fahren). **In einem `SelectionHullScope`** (Aufgaben einer Seite mit
 * mehreren Listen) meldet sie dort nur ihre Zeilen; die Ebene der Seite malt, und die Markierung
 * fährt auch von Liste zu Liste. Unteraufgaben behalten immer ihre eigene Ebene.
 *
 * **Drücken zeigt keine Fläche** (Sven, 09.10.2026): Die gedrückte Zeile verliert nur ihre
 * Hover-Pille (`TaskEntry.scss`, `--hull`), und beim Klick fährt die bisherige Markierung zu ihr.
 *
 * @example
 * <div className="task-list">
 *   <SelectionHull rowIds={taskIds} selectedIds={selectedIds} rowAttribute="data-task-id" rowSelector=".task-entry" />
 *   {rows}
 * </div>
 *
 * @example
 * // Unteraufgaben: eckiger, die bearbeitete Zeile bleibt draußen.
 * <SelectionHull kind="subtask" rowIds={ids} selectedIds={selectedIds} excludedId={editingId} rowAttribute="data-subtask-id" />
 */
export const SelectionHull: React.FC<SelectionHullProps> = ({
  rowIds,
  selectedIds,
  dormantIds,
  excludedId = null,
  rowAttribute,
  rowSelector,
  kind = 'task',
  suspended = false,
}) => {
  const scope = useContext(HullScopeContext)
  const shared = kind === 'task' ? scope : null
  const [ownStore] = useState(() => new HullStore())
  const store = shared?.store ?? ownStore
  const sourceId = useId()
  const elRef = useRef<HTMLDivElement>(null)

  const inIds = useMemo(() => {
    if (excludedId === null || !selectedIds.has(excludedId)) return selectedIds
    const ids = new Set(selectedIds)
    ids.delete(excludedId)
    return ids
  }, [selectedIds, excludedId])

  const selectionKey = useMemo(
    () => [...inIds].sort().map(id => (dormantIds?.has(id) ? `${id}~` : id)).join('\n'),
    [inIds, dormantIds],
  )

  useLayoutEffect(() => {
    store.set(sourceId, { el: elRef.current, rowIds, inIds, excludedId, dormantIds, rowAttribute, rowSelector, selectionKey, suspended })
  }, [store, sourceId, rowIds, inIds, excludedId, dormantIds, rowAttribute, rowSelector, selectionKey, suspended])
  useLayoutEffect(() => () => store.delete(sourceId), [store, sourceId])

  // In einem Scope nur der Anker, an dem die Ebene die Liste findet.
  if (shared) return <div ref={elRef} className="selection-hull" aria-hidden />
  return <HullLayer store={store} kind={kind} layerRef={elRef} />
}
